import React, { useState } from 'react'
import { Sparkles, X } from 'lucide-react'
import { Button } from 'shadcn-components/ui/button'
import { AGENT_APP_HOME_URL, AGENT_MOVE_HELP_URL } from 'services/Utils'

// One-time announcement that the agent app is where Autron happens now
// (autron-agent#629). Dismissed for good rather than for the session: an
// announcement that returns on every reload reads as a nag rather than news.
//
// Gated on the company's DASHBOARD entitlement, not on `freeUser`. freeUser
// counts any live Stripe item as paying, which includes an agent-pack-only
// subscription that product-access deliberately excludes from dashboard
// access — and telling someone with no dashboards that "your dashboards stay
// here" is exactly the sort of thing this banner exists to stop happening
// (joint review, Codex). `has_dashboard_product` is Stripe-derived on the
// same /auth/user payload freeUser already reads.
const DISMISSED_KEY = 'agentMoveBanner.dismissed'

// Reading storage throws outright when the browser blocks site data, and
// this banner sits in the global nav on every authenticated page — an
// unguarded read would take the whole shell down. Degrade to "not
// dismissed" instead.
const readDismissed = () => {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1'
  } catch {
    return false
  }
}

const writeDismissed = () => {
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1')
  } catch {
    // Storage unavailable — the banner just comes back next reload.
  }
}

// Deliberately NOT the warning yellow the other two banners use. This is
// good news, and yellow in this app means "something needs you" — so the
// palette comes from the app's own positive vocabulary instead (the lime
// Alert in views/insights/InsightsDashboard.js and the success toast).
const AgentMoveBanner = ({ user }) => {
  const [isVisible, setIsVisible] = useState(() => !readDismissed())

  if (user?.company?.has_dashboard_product !== true || !isVisible) {
    return null
  }

  const handleClose = () => {
    writeDismissed()
    setIsVisible(false)
  }

  return (
    <div
      role="status"
      data-banner="agent-move"
      aria-live="polite"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 border-b border-lime-300 bg-lime-200 px-4 py-2.5 font-geist text-sm text-slate-800 shadow-sm dark:border-lime-200 dark:bg-transparent dark:text-lime-200"
    >
      <div className="flex min-w-0 flex-1 items-center justify-center gap-2 sm:flex-initial">
        <Sparkles
          className="h-4 w-4 shrink-0 text-slate-800 dark:text-lime-200"
          aria-hidden
        />
        <p className="text-center leading-snug">
          <span className="font-semibold">Autron has moved to the agent.</span>{' '}
          <span className="hidden sm:inline">
            Ask anything, steer the ad engine, act on the answer — included in
            your plan. Your dashboards stay here.
          </span>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <a
          href={AGENT_MOVE_HELP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm underline underline-offset-2 hover:no-underline"
        >
          Learn more
        </a>
        {/* Straight to the app root, in a new tab — the dashboard stays open
            behind them, which is the point of "your dashboards stay here".
            NOT the signed `/auto-login` hand-off: that route rejects any
            referrer outside the dashboard's own origin, so it flashed a
            login page and an "Access Not Allowed!" toast before landing
            anyway. A plain link has no failure mode. */}
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-lime-400 bg-white text-slate-800 hover:bg-lime-50 hover:text-slate-900 dark:bg-transparent dark:text-lime-200"
          asChild
        >
          <a
            href={AGENT_APP_HOME_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open the agent
          </a>
        </Button>
        <button
          type="button"
          onClick={handleClose}
          aria-label="Dismiss"
          className="rounded-md p-1 hover:bg-lime-300 dark:hover:bg-lime-900"
        >
          <X className="h-4 w-4 cursor-pointer text-slate-800 dark:text-lime-200" />
        </button>
      </div>
    </div>
  )
}

export default AgentMoveBanner
